/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CreateRoleDto } from '../models/create-role-dto';
import { RoleDetailsDto } from '../models/role-details-dto';
import { RoleDto } from '../models/role-dto';
import { UpdateRoleDto } from '../models/update-role-dto';


/**
 * API to manage organization's roles. A role can be associated with a set of permissions.
 */
@Injectable({
  providedIn: 'root',
})
export class RolesService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation rolesSearch
   */
  static readonly RolesSearchPath = '/v1/roles';

  /**
   * Permissions: core.roles.search
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `rolesSearch()` instead.
   *
   * This method doesn't expect any request body.
   */
  rolesSearch$Response(params?: {
  }): Observable<StrictHttpResponse<Array<RoleDto>>> {

    const rb = new RequestBuilder(this.rootUrl, RolesService.RolesSearchPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RoleDto>>;
      })
    );
  }

  /**
   * Permissions: core.roles.search
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `rolesSearch$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  rolesSearch(params?: {
  }): Observable<Array<RoleDto>> {

    return this.rolesSearch$Response(params).pipe(
      map((r: StrictHttpResponse<Array<RoleDto>>) => r.body as Array<RoleDto>)
    );
  }

  /**
   * Path part for operation rolesPost
   */
  static readonly RolesPostPath = '/v1/roles';

  /**
   * Permissions: core.roles.create
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `rolesPost()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  rolesPost$Response(params: {
    body: CreateRoleDto
  }): Observable<StrictHttpResponse<number>> {

    const rb = new RequestBuilder(this.rootUrl, RolesService.RolesPostPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: parseFloat(String((r as HttpResponse<any>).body)) }) as StrictHttpResponse<number>;
      })
    );
  }

  /**
   * Permissions: core.roles.create
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `rolesPost$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  rolesPost(params: {
    body: CreateRoleDto
  }): Observable<number> {

    return this.rolesPost$Response(params).pipe(
      map((r: StrictHttpResponse<number>) => r.body as number)
    );
  }

  /**
   * Path part for operation rolesGet
   */
  static readonly RolesGetPath = '/v1/roles/{id}';

  /**
   * Permissions: core.roles.get
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `rolesGet()` instead.
   *
   * This method doesn't expect any request body.
   */
  rolesGet$Response(params: {
    id: number;
  }): Observable<StrictHttpResponse<RoleDetailsDto>> {

    const rb = new RequestBuilder(this.rootUrl, RolesService.RolesGetPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RoleDetailsDto>;
      })
    );
  }

  /**
   * Permissions: core.roles.get
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `rolesGet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  rolesGet(params: {
    id: number;
  }): Observable<RoleDetailsDto> {

    return this.rolesGet$Response(params).pipe(
      map((r: StrictHttpResponse<RoleDetailsDto>) => r.body as RoleDetailsDto)
    );
  }

  /**
   * Path part for operation rolesPut
   */
  static readonly RolesPutPath = '/v1/roles/{id}';

  /**
   * Permissions: core.roles.update
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `rolesPut()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  rolesPut$Response(params: {
    id: number;
    body: UpdateRoleDto
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, RolesService.RolesPutPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Permissions: core.roles.update
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `rolesPut$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  rolesPut(params: {
    id: number;
    body: UpdateRoleDto
  }): Observable<void> {

    return this.rolesPut$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation rolesDelete
   */
  static readonly RolesDeletePath = '/v1/roles/{id}';

  /**
   * Permissions: core.roles.delete
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `rolesDelete()` instead.
   *
   * This method doesn't expect any request body.
   */
  rolesDelete$Response(params: {
    id: number;
    concurrencyToken?: number;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, RolesService.RolesDeletePath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('concurrencyToken', params.concurrencyToken, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Permissions: core.roles.delete
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `rolesDelete$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  rolesDelete(params: {
    id: number;
    concurrencyToken?: number;
  }): Observable<void> {

    return this.rolesDelete$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
